import React, { useCallback, useEffect, useState } from 'react';
import { auditAPI, usersAPI } from '../../services/api';
import { fmtDate } from '../../utils/dateUtils';
import { parseApiError } from '../../utils/errors';

// Friendly labels for the raw action names stored in the audit log
const ACTION_LABELS = {
  login_success: 'Signed in',
  login_failed: 'Failed sign-in',
  create_user: 'Created user',
  update_user: 'Updated user',
  deactivate_user: 'Deactivated user',
  activate_user: 'Activated user',
  change_password: 'Changed password',
  admin_reset_password: 'Reset a password',
  create_task: 'Created task',
  update_task: 'Updated task',
  assign_task: 'Assigned task',
  hr_update_task: 'Reviewed task',
  hr_edit_task: 'Edited task',
  delete_task: 'Deleted task',
  create_issue: 'Raised issue',
  update_issue: 'Updated issue',
  create_concern: 'Submitted concern',
  respond_concern: 'Responded to concern',
};

const ACTION_COLOR = (a) => {
  if (a.includes('delete') || a.includes('failed') || a.includes('deactivate')) return 'red';
  if (a.includes('create') || a.includes('assign')) return 'green';
  if (a.includes('update') || a.includes('edit') || a.includes('reset')) return 'orange';
  if (a.includes('login')) return 'blue';
  return 'gray';
};

export default function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [fUser, setFUser] = useState('');
  const [fAction, setFAction] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const load = useCallback(() => {
    const params = { limit: 300 };
    if (fUser) params.user_id = parseInt(fUser);
    if (fAction) params.action = fAction;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    setLoading(true);
    auditAPI.list(params)
      .then(({ data }) => setLogs(data))
      .catch((e) => setError(parseApiError(e, 'Failed to load activity log')))
      .finally(() => setLoading(false));
  }, [fUser, fAction, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    usersAPI.list().then(({ data }) => setPeople(data)).catch(() => {});
    auditAPI.actions().then(({ data }) => setActions(data)).catch(() => {});
  }, []);

  const reset = () => { setFUser(''); setFAction(''); setFromDate(''); setToDate(''); };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Activity Log</h2>
        <button className="btn btn-ghost" onClick={load}>↻ Refresh</button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="filter-bar">
        <select value={fUser} onChange={(e) => setFUser(e.target.value)}>
          <option value="">All Users</option>
          {people.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
        </select>
        <select value={fAction} onChange={(e) => setFAction(e.target.value)}>
          <option value="">All Actions</option>
          {actions.map((a) => <option key={a} value={a}>{ACTION_LABELS[a] || a.replace(/_/g, ' ')}</option>)}
        </select>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} title="From date" />
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} title="To date" />
        <button className="btn btn-ghost" onClick={reset}>Clear</button>
      </div>

      {loading ? <div className="page-loading">Loading…</div> : (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr><th>When</th><th>User</th><th>Action</th><th>Resource</th><th>Details</th><th>IP</th></tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-muted">No activity found</td></tr>
              ) : logs.map((l) => (
                <tr key={l.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(l.timestamp)}</td>
                  <td>{l.user?.full_name || <span className="text-muted">—</span>}</td>
                  <td>
                    <span className={`badge badge-${ACTION_COLOR(l.action)}`}>
                      {ACTION_LABELS[l.action] || l.action.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="text-muted">
                    {l.resource_type ? `${l.resource_type}${l.resource_id ? ` #${l.resource_id}` : ''}` : '—'}
                  </td>
                  <td style={{ maxWidth: 320, overflowWrap: 'anywhere' }}>{l.details || '—'}</td>
                  <td className="text-muted" style={{ fontSize: 12 }}>{l.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
